import { createSignal, createMemo, onMount, For, Show } from 'solid-js';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import Text from '../components/Text';
import {
    type VoteAdminLogDto,
    type VoteAdminTodayDto,
    type VoteAdminTodayRowDto,
    type VotingSchedulerStatusDto,
    type VoteSiteDto,
    type VotingTickReportDto,
    voteOutcomeKey,
    isPositiveOutcome,
} from '../services/votingApi';
import {
    type VoteAdminStatsDto,
    type VoteAdminBotStatDto,
    type VoteAdminAdHocStatDto,
    type VoteAdminDailyStatDto,
} from '../api/generated/schemas';
import { formatDateTime, formatDate } from '../services/dateFormat';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import styles from './Voting.module.css';
import { firstError } from '../api/envelope';

const POLL_MS = 15_000;

type RangePreset = 'today' | '7d' | '30d' | 'custom';

function toIsoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
}

export default function AdminVoting() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [logs, setLogs] = createStore<VoteAdminLogDto[]>([]);
    const [today, setToday] = createStore<VoteAdminTodayRowDto[]>([]);
    const [sites, setSites] = createStore<VoteSiteDto[]>([]);
    const [dayStart, setDayStart] = createSignal<string | null>(null);
    const [scheduler, setScheduler] = createSignal<VotingSchedulerStatusDto | null>(null);
    const [statsStore, setStatsStore] = createStore<{ current: VoteAdminStatsDto | null }>({ current: null });
    const stats = () => statsStore.current;
    const [loading, setLoading] = createSignal(true);

    const [rangePreset, setRangePreset] = createSignal<RangePreset>('7d');
    const [customFrom, setCustomFrom] = createSignal(toIsoDate(new Date(Date.now() - 7 * 86_400_000)));
    const [customTo, setCustomTo] = createSignal(toIsoDate(new Date()));

    const [addOpen, setAddOpen] = createSignal(false);
    const [addUsername, setAddUsername] = createSignal('');
    const [addSubmitting, setAddSubmitting] = createSignal(false);
    const [tickRunning, setTickRunning] = createSignal(false);
    const [botBusyId, setBotBusyId] = createSignal<string | null>(null);
    const [adHocBusyId, setAdHocBusyId] = createSignal<string | null>(null);

    const unwrapList = <T,>(v: unknown, key: string): T[] => {
        if (Array.isArray(v)) return v as T[];
        if (v && typeof v === 'object' && Array.isArray((v as Record<string, unknown>)[key])) {
            return (v as Record<string, unknown>)[key] as T[];
        }
        return [];
    };

    const computeRange = (): { fromUtc: string; toUtc: string } => {
        const now = new Date();
        if (rangePreset() === 'today') {
            const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
            return { fromUtc: start.toISOString(), toUtc: now.toISOString() };
        }
        if (rangePreset() === 'custom' && customFrom() && customTo()) {
            return { fromUtc: `${customFrom()}T00:00:00.000Z`, toUtc: `${customTo()}T23:59:59.999Z` };
        }
        const days = rangePreset() === '30d' ? 30 : 7;
        const from = new Date(now.getTime() - days * 86_400_000);
        return { fromUtc: from.toISOString(), toUtc: now.toISOString() };
    };

    const fetchAll = async () => {
        const { fromUtc, toUtc } = computeRange();
        const [logsRes, todayRes, schedulerRes, sitesRes, statsRes] = await Promise.all([
            api.voting.adminLog({ take: 100 }),
            api.voting.adminToday(),
            api.voting.schedulerStatus(),
            api.voting.getSites(),
            api.voting.adminStats({ fromUtc, toUtc }),
        ]);
        if (isOk(logsRes.data)) setLogs(reconcile(unwrapList<VoteAdminLogDto>(logsRes.data.value, 'entries'), { key: 'id' }));
        if (isOk(todayRes.data)) {
            const dto = todayRes.data.value as unknown as VoteAdminTodayDto;
            setDayStart(dto.votingDayStartUtc);
            setToday(reconcile(dto.rows, { key: 'id' }));
        }
        if (isOk(schedulerRes.data)) setScheduler(schedulerRes.data.value as unknown as VotingSchedulerStatusDto);
        if (isOk(sitesRes.data)) setSites(reconcile(unwrapList<VoteSiteDto>(sitesRes.data.value, 'sites'), { key: 'id' }));
        if (isOk(statsRes.data)) setStatsStore('current', reconcile(statsRes.data.value as unknown as VoteAdminStatsDto, { key: 'botRegistryId' }));
        if (loading()) setLoading(false);
    };

    const selectRange = (preset: RangePreset) => {
        setRangePreset(preset);
        void fetchAll();
    };

    const applyCustomRange = () => {
        setRangePreset('custom');
        void fetchAll();
    };

    const submitAdHoc = async () => {
        const name = addUsername().trim();
        if (!name) {
            toast?.error(t('ADMIN_VOTING_PICK_USER'));
            return;
        }
        setAddSubmitting(true);
        const res = await api.voting.adHocCreate({ minecraftUsername: name });
        setAddSubmitting(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_ADD_FAILED'));
            return;
        }
        toast?.success(t('ADMIN_VOTING_ADHOC_ENABLED', name));
        setAddOpen(false);
        setAddUsername('');
        void fetchAll();
    };

    const runSchedulerTick = async () => {
        const ok = await showConfirm(t('ADMIN_VOTING_TICK_CONFIRM'));
        if (!ok) return;
        setTickRunning(true);
        const res = await api.voting.runTick();
        setTickRunning(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_TICK_FAILED'));
            return;
        }
        const r = res.data.value as unknown as VotingTickReportDto;
        toast?.success(t('ADMIN_VOTING_TICK_RESULT', r.attempted));
        void fetchAll();
    };

    const [resettingFailures, setResettingFailures] = createSignal(false);
    const resetAllFailures = async () => {
        const ok = await showConfirm(t('ADMIN_VOTING_RESET_CONFIRM'), true);
        if (!ok) return;
        setResettingFailures(true);
        const res = await api.voting.adminResetFailures();
        setResettingFailures(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_RESET_FAILED')); return; }
        const r = res.data.value as unknown as { touched: number };
        toast?.info(t('ADMIN_VOTING_RESET_RESULT', r.touched));
        void fetchAll();
    };

    const toggleSite = async (siteKey: string, enabled: boolean) => {
        const res = await api.voting.setSiteEnabled(siteKey, { enabled });
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_SITE_TOGGLE_FAILED'));
            return;
        }
        toast?.info(enabled ? t('ADMIN_VOTING_SITE_ENABLED', siteKey) : t('ADMIN_VOTING_SITE_DISABLED', siteKey));
        void fetchAll();
    };

    const clearStale = async (botId: string, siteKey: string) => {
        const res = await api.voting.adminClearStale(botId, siteKey);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_CLEAR_STALE_FAILED')); return; }
        toast?.info(t('ADMIN_VOTING_STALE_CLEARED'));
        void fetchAll();
    };

    const forceStale = async (botId: string, siteKey: string) => {
        const ok = await showConfirm(t('ADMIN_VOTING_FORCE_STALE_CONFIRM'));
        if (!ok) return;
        const res = await api.voting.adminForceStale(botId, siteKey);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_FORCE_STALE_FAILED')); return; }
        toast?.info(t('ADMIN_VOTING_PAIR_STALE'));
        void fetchAll();
    };

    const removeAdHoc = async (rowId: string, displayName: string) => {
        const id = rowId.startsWith('adhoc:') ? rowId.substring(6).replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5') : rowId;
        const ok = await showConfirm(t('ADMIN_VOTING_REMOVE_CONFIRM', displayName), true);
        if (!ok) return;
        const res = await api.voting.adHocDelete(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('ADMIN_VOTING_REMOVE_FAILED'));
            return;
        }
        toast?.success(t('ADMIN_VOTING_REMOVED'));
        void fetchAll();
    };

    const disableBotVoting = async (bot: VoteAdminBotStatDto) => {
        const ok = await showConfirm(t('ADMIN_VOTING_BOT_DISABLE_CONFIRM', bot.minecraftUsername), true);
        if (!ok) return;
        setBotBusyId(bot.botRegistryId);
        const res = await api.botRegistry.setVoting(bot.botRegistryId, { enabled: false });
        setBotBusyId(null);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('VOTING_TOGGLE_FAILED')); return; }
        toast?.info(t('VOTING_DISABLED'));
        void fetchAll();
    };

    const toggleAdHocTarget = async (target: VoteAdminAdHocStatDto) => {
        setAdHocBusyId(target.targetId);
        const res = await api.voting.adHocSetEnabled(target.targetId, { enabled: !target.isEnabled });
        setAdHocBusyId(null);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('VOTING_TOGGLE_FAILED')); return; }
        toast?.info(target.isEnabled ? t('VOTING_DISABLED') : t('VOTING_ENABLED'));
        void fetchAll();
    };

    onMount(() => {
        void fetchAll();
        useVisibilityPoll(() => void fetchAll(), POLL_MS);
    });

    const successCount = () => logs.filter(l => isPositiveOutcome(l.outcome)).length;

    const expandBotIdFromRowId = (rowId: string): string | null => {
        if (rowId.startsWith('adhoc:')) return null;
        const i = rowId.indexOf('|');
        if (i !== 32) return null;
        const hex = rowId.substring(0, 32);
        return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20, 32)}`;
    };

    const todayDone = () => today.filter(r => r.status === 'Voted').length;
    const todayPending = () => today.filter(r => r.status === 'Pending').length;
    const todayFailed = () => today.filter(r => r.status === 'Failed').length;
    const todayBlocked = () => today.filter(r => r.status === 'ProxyDown' || r.status === 'Stale' || r.status === 'Disabled').length;
    const redeemedCount = () => today.filter(r => r.status === 'Voted' && r.redeemed).length;

    const statusBadgeClass = (status: string) => {
        switch (status) {
            case 'Voted': return styles.outcomeOk;
            case 'Pending': return '';
            case 'Failed':
            case 'ProxyDown':
            case 'Stale':
                return styles.outcomeBad;
            case 'Disabled': return '';
            default: return '';
        }
    };

    const dailyMax = createMemo(() => {
        const daily = stats()?.daily ?? [];
        return Math.max(1, ...daily.map((d: VoteAdminDailyStatDto) => d.attempts));
    });

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="ADMIN_VOTING_TITLE" /></h1>
                <span class={styles.entryCount}>{today.length} <Text val="ADMIN_VOTING_PAIRS" /></span>
            </div>

            <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-lg)' }}>
                <h2 style={{ margin: 0 }}><Text val="ADMIN_VOTING_STATS_HEADER" /></h2>
                <div class={styles.rangeSelector}>
                    <button class={`${styles.rangeButton} ${rangePreset() === 'today' ? styles.rangeButtonActive : ''}`} onClick={() => selectRange('today')}>
                        <Text val="ADMIN_VOTING_RANGE_TODAY" />
                    </button>
                    <button class={`${styles.rangeButton} ${rangePreset() === '7d' ? styles.rangeButtonActive : ''}`} onClick={() => selectRange('7d')}>
                        <Text val="ADMIN_VOTING_RANGE_7D" />
                    </button>
                    <button class={`${styles.rangeButton} ${rangePreset() === '30d' ? styles.rangeButtonActive : ''}`} onClick={() => selectRange('30d')}>
                        <Text val="ADMIN_VOTING_RANGE_30D" />
                    </button>
                    <button class={`${styles.rangeButton} ${rangePreset() === 'custom' ? styles.rangeButtonActive : ''}`} onClick={() => setRangePreset('custom')}>
                        <Text val="ADMIN_VOTING_RANGE_CUSTOM" />
                    </button>
                </div>
            </div>

            <Show when={rangePreset() === 'custom'}>
                <div class="fcard h" style={{ 'align-items': 'flex-end', gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-lg)' }}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ADMIN_VOTING_RANGE_FROM" /></span>
                        <input type="date" value={customFrom()} onInput={(e) => setCustomFrom(e.currentTarget.value)} />
                    </label>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ADMIN_VOTING_RANGE_TO" /></span>
                        <input type="date" value={customTo()} onInput={(e) => setCustomTo(e.currentTarget.value)} />
                    </label>
                    <button class="btn btn-primary btn-sm" onClick={applyCustomRange}><Text val="ADMIN_VOTING_RANGE_APPLY" /></button>
                </div>
            </Show>

            <div class="fcard v" style={{ gap: 'var(--spacing-lg)', 'margin-bottom': 'var(--spacing-lg)' }}>
                <div class={styles.statRow}>
                    <div class={styles.statTile}>
                        <span class={styles.statLabel}><Text val="ADMIN_VOTING_STATS_RANGE_ATTEMPTS" /></span>
                        <span class={styles.statValue}>{stats()?.range.attempts ?? 0}</span>
                    </div>
                    <div class={styles.statTile}>
                        <span class={styles.statLabel}><Text val="ADMIN_VOTING_STATS_RANGE_SUCCESS_RATE" /></span>
                        <span class={`${styles.statValue} ${styles.statValueSuccess}`}>{stats()?.range.successRatePercent ?? 0}%</span>
                    </div>
                    <div class={styles.statTile}>
                        <span class={styles.statLabel}><Text val="ADMIN_VOTING_STATS_RANGE_FAILED" /></span>
                        <span class={`${styles.statValue} ${(stats()?.range.failed ?? 0) > 0 ? styles.statValueError : ''}`}>{stats()?.range.failed ?? 0}</span>
                    </div>
                    <div class={styles.statTile}>
                        <span class={styles.statLabel}><Text val="ADMIN_VOTING_STATS_TODAY_ATTEMPTS" /></span>
                        <span class={styles.statValue}>{stats()?.today.attempts ?? 0}</span>
                    </div>
                    <div class={styles.statTile}>
                        <span class={styles.statLabel}><Text val="ADMIN_VOTING_STATS_TODAY_SUCCESS_RATE" /></span>
                        <span class={`${styles.statValue} ${styles.statValueSuccess}`}>{stats()?.today.successRatePercent ?? 0}%</span>
                    </div>
                    <div class={styles.statTile}>
                        <span class={styles.statLabel}><Text val="ADMIN_VOTING_STATS_TODAY_FAILED" /></span>
                        <span class={`${styles.statValue} ${(stats()?.today.failed ?? 0) > 0 ? styles.statValueError : ''}`}>{stats()?.today.failed ?? 0}</span>
                    </div>
                </div>

                <Show when={(stats()?.daily.length ?? 0) > 0}>
                    <div>
                        <div class={styles.dailyChart}>
                            <For each={stats()!.daily}>{(d) => {
                                const heightPct = () => Math.round((d.attempts / dailyMax()) * 100);
                                const successPct = () => d.attempts === 0 ? 0 : Math.round((d.success / d.attempts) * 100);
                                return (
                                    <div
                                        class={styles.dailyBar}
                                        style={{ height: `${Math.max(heightPct(), d.attempts > 0 ? 4 : 0)}%` }}
                                        title={`${formatDate(d.date)}: ${d.success} ${t('ADMIN_VOTING_STATS_RANGE_SUCCESS_RATE')}, ${d.failed} ${t('ADMIN_VOTING_STATS_RANGE_FAILED')}`}>
                                        <div class={styles.dailyBarSuccess} style={{ height: `${successPct()}%` }} />
                                        <div class={styles.dailyBarFailed} style={{ height: `${100 - successPct()}%` }} />
                                    </div>
                                );
                            }}</For>
                        </div>
                        <div class={styles.dailyLabels}>
                            <For each={stats()!.daily}>{(d) => <span class={styles.dailyLabel}>{formatDate(d.date)}</span>}</For>
                        </div>
                    </div>
                </Show>
            </div>

            <Show when={scheduler()}>
                {(s) => (
                    <div class="fcard h" style={{ 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-lg)', 'margin-bottom': 'var(--spacing-lg)' }}>
                        <div>
                            <span class={`${styles.outcomeBadge} ${s().enabled ? styles.outcomeOk : styles.outcomeBad}`}>
                                <Text val={s().enabled ? 'VOTING_SCHEDULER_ENABLED' : 'VOTING_SCHEDULER_DISABLED'} />
                            </span>
                        </div>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="VOTING_SCHEDULER_WINDOW" /></span>
                            <span style={{ 'font-family': 'var(--font-family-mono)' }}>
                                {String(s().windowStartHourLocal).padStart(2, '0')}:00 - {String(s().windowEndHourLocal).padStart(2, '0')}:00
                            </span>
                        </div>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="VOTING_SCHEDULER_LAST_TICK" /></span>
                            <span>{formatDateTime(s().lastTickStartUtc)}</span>
                        </div>
                        <Show when={s().currentSiteKey}>
                            <div style={{ color: 'var(--primary-400)' }}>
                                <i class="fa-solid fa-spinner fa-spin" /> <Text val="VOTING_SCHEDULER_RUNNING" />: {s().currentSiteKey}
                            </div>
                        </Show>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_DAY_STARTED" /></span>
                            <span>{formatDateTime(dayStart())}</span>
                        </div>
                        <div class="h" style={{ 'margin-left': 'auto', gap: 'var(--spacing-sm)' }}>
                            <button class="btn btn-secondary btn-sm" disabled={resettingFailures()} onClick={resetAllFailures}>
                                <Show when={resettingFailures()} fallback={<><i class="fa-solid fa-rotate-left" /> <Text val="ADMIN_VOTING_RESET_FAILURES" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="ADMIN_VOTING_RESETTING" />
                                </Show>
                            </button>
                            <button class="btn btn-secondary btn-sm" disabled={tickRunning()} onClick={runSchedulerTick}>
                                <Show when={tickRunning()} fallback={<><i class="fa-solid fa-bolt" /> <Text val="VOTING_RUN_TICK" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="VOTING_RUN_TICK_RUNNING" />
                                </Show>
                            </button>
                        </div>
                    </div>
                )}
            </Show>

            <h2 style={{ 'margin-top': 'var(--spacing-lg)' }}><Text val="ADMIN_VOTING_SITES_HEADER" /></h2>
            <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fill, minmax(18rem, 1fr))', gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-lg)' }}>
                <For each={sites}>{(site) => {
                    const siteStat = () => stats()?.bySite.find(s => s.siteKey === site.key);
                    return (
                        <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center' }}>
                                <span style={{ 'font-weight': 600 }}>{site.displayName}</span>
                                <button
                                    class={`btn btn-sm ${site.isEnabled ? 'btn-secondary' : 'btn-primary'}`}
                                    onClick={() => toggleSite(site.key, !site.isEnabled)}>
                                    <Text val={site.isEnabled ? 'ADMIN_VOTING_SITE_DISABLE' : 'ADMIN_VOTING_SITE_ENABLE'} />
                                </button>
                            </div>
                            <Show when={siteStat()} fallback={
                                <div style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                    <Text val="ADMIN_VOTING_STATS_NO_DATA" />
                                </div>
                            }>
                                {(stat) => (
                                    <div style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                        <span style={{ color: 'var(--success-500)' }}>{stat().success}</span> / {stat().attempts} <Text val="ADMIN_VOTING_ATTEMPTS" />
                                        <Show when={stat().failed > 0}>{' · '}<span style={{ color: 'var(--error-500)' }}>{stat().failed} <Text val="VOTING_FAILED" /></span></Show>
                                        {' · '}{stat().successRatePercent}%
                                    </div>
                                )}
                            </Show>
                        </div>
                    );
                }}</For>
            </div>

            <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between', 'margin-top': 'var(--spacing-2xl)' }}>
                <h2 style={{ margin: 0 }}><Text val="ADMIN_VOTING_BOT_STATS_HEADER" /></h2>
            </div>
            <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                <Show when={(stats()?.byBot.length ?? 0) > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-robot" />
                        <p><Text val="ADMIN_VOTING_BOT_STATS_EMPTY" /></p>
                    </div>
                }>
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                            <table class={styles.logTable}>
                                <thead>
                                    <tr>
                                        <th><Text val="ADMIN_VOTING_BOT_REDACTED" /></th>
                                        <th><Text val="ADMIN_VOTING_OWNER" /></th>
                                        <th><Text val="ADMIN_VOTING_ATTEMPTS" /></th>
                                        <th><Text val="ADMIN_VOTING_STATS_RANGE_FAILED" /></th>
                                        <th><Text val="ADMIN_VOTING_STATS_RANGE_SUCCESS_RATE" /></th>
                                        <th><Text val="ADMIN_VOTING_LAST_VOTE" /></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={stats()?.byBot ?? []}>{(bot) => (
                                        <tr>
                                            <td style={{ 'font-family': 'var(--font-family-mono)' }}>{bot.minecraftUsername}</td>
                                            <td class={styles.ownerCell}>{bot.ownerUsername}</td>
                                            <td>{bot.attempts}</td>
                                            <td style={{ color: bot.failed > 0 ? 'var(--error-500)' : undefined }}>{bot.failed}</td>
                                            <td>{bot.successRatePercent}%</td>
                                            <td>{formatDateTime(bot.lastVoteUtc)}</td>
                                            <td>
                                                <button class="btn btn-danger btn-sm" disabled={botBusyId() === bot.botRegistryId}
                                                    onClick={() => void disableBotVoting(bot)}>
                                                    <Show when={botBusyId() === bot.botRegistryId} fallback={<Text val="VOTING_TURN_OFF" />}>
                                                        <i class="fa-solid fa-spinner fa-spin" />
                                                    </Show>
                                                </button>
                                            </td>
                                        </tr>
                                    )}</For>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Show>
            </Show>

            <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between', 'margin-top': 'var(--spacing-2xl)' }}>
                <h2 style={{ margin: 0 }}><Text val="ADMIN_VOTING_ADHOC_STATS_HEADER" /></h2>
                <button class="btn btn-primary btn-sm" onClick={() => setAddOpen(v => !v)}>
                    <i class="fa-solid fa-plus" /> <Text val="ADMIN_VOTING_ADD_ADHOC" />
                </button>
            </div>

            <Show when={addOpen()}>
                <form class="fcard v" style={{ gap: 'var(--spacing-md)', 'margin-top': 'var(--spacing-md)' }}
                    onSubmit={(e) => { e.preventDefault(); void submitAdHoc(); }}>
                    <h3 style={{ margin: 0 }}><Text val="ADMIN_VOTING_ADD_ADHOC_TITLE" /></h3>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ADMIN_VOTING_BOT_REDACTED" /></span>
                        <input type="text" placeholder="Aktenkoffer_" value={addUsername()} onInput={(e) => setAddUsername(e.currentTarget.value)} />
                    </label>
                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-secondary" onClick={() => setAddOpen(false)}><Text val="CANCEL" /></button>
                        <button type="submit" class="btn btn-primary" disabled={addSubmitting() || !addUsername().trim()}>
                            <Show when={addSubmitting()} fallback={<Text val="ADMIN_VOTING_ADD_ADHOC" />}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                    </div>
                </form>
            </Show>

            <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                <Show when={(stats()?.byAdHoc.length ?? 0) > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-user-plus" />
                        <p><Text val="ADMIN_VOTING_ADHOC_STATS_EMPTY" /></p>
                    </div>
                }>
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                            <table class={styles.logTable}>
                                <thead>
                                    <tr>
                                        <th><Text val="ADMIN_VOTING_BOT_REDACTED" /></th>
                                        <th><Text val="ADMIN_VOTING_OWNER" /></th>
                                        <th><Text val="ADMIN_VOTING_ATTEMPTS" /></th>
                                        <th><Text val="ADMIN_VOTING_STATS_RANGE_FAILED" /></th>
                                        <th><Text val="ADMIN_VOTING_STATS_RANGE_SUCCESS_RATE" /></th>
                                        <th><Text val="ADMIN_VOTING_LAST_VOTE" /></th>
                                        <th><Text val="ADMIN_VOTING_TODAY_STATUS" /></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={stats()?.byAdHoc ?? []}>{(target) => (
                                        <tr>
                                            <td style={{ 'font-family': 'var(--font-family-mono)' }}>{target.minecraftUsername}</td>
                                            <td class={styles.ownerCell}>{target.ownerUsername}</td>
                                            <td>{target.attempts}</td>
                                            <td style={{ color: target.failed > 0 ? 'var(--error-500)' : undefined }}>{target.failed}</td>
                                            <td>{target.successRatePercent}%</td>
                                            <td>{formatDateTime(target.lastVoteUtc)}</td>
                                            <td>
                                                <span class={`${styles.outcomeBadge} ${target.isEnabled ? styles.outcomeOk : styles.outcomeBad}`}>
                                                    <Text val={target.isEnabled ? 'ADMIN_VOTING_ADHOC_ENABLED_BADGE' : 'ADMIN_VOTING_STATUS_DISABLED'} />
                                                </span>
                                            </td>
                                            <td>
                                                <div class={styles.actions}>
                                                    <button class={`btn btn-sm ${target.isEnabled ? 'btn-secondary' : 'btn-primary'}`}
                                                        disabled={adHocBusyId() === target.targetId}
                                                        onClick={() => void toggleAdHocTarget(target)}>
                                                        <Show when={adHocBusyId() === target.targetId} fallback={
                                                            <Text val={target.isEnabled ? 'VOTING_TURN_OFF' : 'VOTING_TURN_ON'} />
                                                        }>
                                                            <i class="fa-solid fa-spinner fa-spin" />
                                                        </Show>
                                                    </button>
                                                    <button class="btn btn-danger btn-sm" title={t('ADMIN_VOTING_REMOVE_TITLE')}
                                                        onClick={() => void removeAdHoc(`adhoc:${target.targetId.replace(/-/g, '')}`, target.minecraftUsername)}>
                                                        <i class="fa-solid fa-trash" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}</For>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Show>
            </Show>

            <div class="fcard h" style={{ 'flex-wrap': 'wrap', gap: 'var(--spacing-xl)', 'margin-top': 'var(--spacing-2xl)', 'margin-bottom': 'var(--spacing-lg)' }}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_TODAY_DONE" /></span>
                    <span style={{ 'font-size': 'var(--font-size-xl)', 'font-weight': 600, color: 'var(--success-500)' }}>{todayDone()}/{today.length}</span>
                </div>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_TODAY_PENDING" /></span>
                    <span style={{ 'font-size': 'var(--font-size-xl)', 'font-weight': 600 }}>{todayPending()}</span>
                </div>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_TODAY_FAILED" /></span>
                    <span style={{ 'font-size': 'var(--font-size-xl)', 'font-weight': 600, color: todayFailed() > 0 ? 'var(--error-500)' : undefined }}>{todayFailed()}</span>
                </div>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_TODAY_BLOCKED" /></span>
                    <span style={{ 'font-size': 'var(--font-size-xl)', 'font-weight': 600 }}>{todayBlocked()}</span>
                </div>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_TODAY_REDEEMED" /></span>
                    <span style={{ 'font-size': 'var(--font-size-xl)', 'font-weight': 600 }}>{redeemedCount()}/{todayDone()}</span>
                </div>
            </div>

            <h2 style={{ margin: 0 }}><Text val="ADMIN_VOTING_TODAY_HEADER" /></h2>
            <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /><div class={styles.skeleton} /></div>}>
                <Show when={today.length > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-clipboard-list" />
                        <p><Text val="ADMIN_VOTING_TODAY_EMPTY" /></p>
                    </div>
                }>
                    <div class="fcard" style={{ 'margin-top': 'var(--spacing-md)' }}>
                        <div class={styles.tableWrap}>
                        <table class={styles.logTable}>
                            <thead>
                                <tr>
                                    <th><Text val="ADMIN_VOTING_BOT_REDACTED" /></th>
                                    <th><Text val="ADMIN_VOTING_OWNER" /></th>
                                    <th><Text val="VOTING_LOG_SITE" /></th>
                                    <th><Text val="ADMIN_VOTING_TODAY_STATUS" /></th>
                                    <th><Text val="VOTING_LOG_OUTCOME" /></th>
                                    <th><Text val="ADMIN_VOTING_TODAY_VOTED_AT" /></th>
                                    <th><Text val="ADMIN_VOTING_TODAY_REDEEMED_AT" /></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <For each={today}>{(r) => (
                                    <tr>
                                        <td style={{ 'font-family': 'var(--font-family-mono)' }}>
                                            {r.minecraftUsernameRedacted}
                                            <Show when={r.isAdHoc}>
                                                <span class={styles.outcomeBadge} style={{ 'margin-left': 'var(--spacing-sm)' }}><Text val="ADMIN_VOTING_ADHOC_BADGE" /></span>
                                            </Show>
                                        </td>
                                        <td class={styles.ownerCell}>{r.ownerUsername ?? '--'}</td>
                                        <td>{r.siteKey}</td>
                                        <td>
                                            <span class={`${styles.outcomeBadge} ${statusBadgeClass(r.status)}`}>
                                                <Text val={`ADMIN_VOTING_STATUS_${r.status.toUpperCase()}`} />
                                            </span>
                                        </td>
                                        <td>
                                            <Show when={r.outcome != null} fallback={<span style={{ color: 'var(--dark-text-muted)' }}>--</span>}>
                                                <Text val={voteOutcomeKey(r.outcome!)} />
                                            </Show>
                                        </td>
                                        <td>{formatDateTime(r.lastVoteUtc)}</td>
                                        <td>
                                            <Show when={r.status === 'Voted'} fallback={<span style={{ color: 'var(--dark-text-muted)' }}>--</span>}>
                                                <Show when={r.redeemed} fallback={
                                                    <span style={{ color: 'var(--warning-500)', 'white-space': 'nowrap', display: 'inline-flex', 'align-items': 'center', gap: 'var(--spacing-xs)' }}>
                                                        <i class="fa-solid fa-clock" /><Text val="ADMIN_VOTING_REDEEM_PENDING" />
                                                    </span>
                                                }>
                                                    <span style={{ color: 'var(--success-500)', 'white-space': 'nowrap', display: 'inline-flex', 'align-items': 'center', gap: 'var(--spacing-xs)' }}>
                                                        <i class="fa-solid fa-check" />{formatDateTime(r.lastRedeemedUtc)}
                                                    </span>
                                                </Show>
                                            </Show>
                                        </td>
                                        <td>
                                            <Show when={r.isAdHoc}>
                                                <button class="btn btn-danger btn-sm" title={t('ADMIN_VOTING_REMOVE_TITLE')}
                                                    onClick={() => void removeAdHoc(r.id, r.minecraftUsernameRedacted)}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </Show>
                                            <Show when={!r.isAdHoc && expandBotIdFromRowId(r.id)}>
                                                <Show when={r.isStale} fallback={
                                                    <button class="btn btn-secondary btn-sm" title={t('ADMIN_VOTING_FORCE_STALE_TITLE')}
                                                        onClick={() => void forceStale(expandBotIdFromRowId(r.id)!, r.siteKey)}>
                                                        <i class="fa-solid fa-pause" />
                                                    </button>
                                                }>
                                                    <button class="btn btn-primary btn-sm" title={t('ADMIN_VOTING_CLEAR_STALE_TITLE')}
                                                        onClick={() => void clearStale(expandBotIdFromRowId(r.id)!, r.siteKey)}>
                                                        <i class="fa-solid fa-rotate" />
                                                    </button>
                                                </Show>
                                            </Show>
                                        </td>
                                    </tr>
                                )}</For>
                            </tbody>
                        </table>
                        </div>
                    </div>
                </Show>
            </Show>

            <h2 style={{ 'margin-top': 'var(--spacing-2xl)' }}><Text val="ADMIN_VOTING_RECENT_HEADER" /></h2>
            <div class="fcard h" style={{ 'flex-wrap': 'wrap', gap: 'var(--spacing-xl)', 'margin-bottom': 'var(--spacing-md)' }}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ADMIN_VOTING_SUCCESSES" /></span>
                    <span style={{ 'font-size': 'var(--font-size-xl)', 'font-weight': 600, color: 'var(--success-500)' }}>{successCount()}/{logs.length}</span>
                </div>
            </div>
            <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                <Show when={logs.length > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-list" />
                        <p><Text val="ADMIN_VOTING_EMPTY" /></p>
                    </div>
                }>
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                        <table class={styles.logTable}>
                            <thead>
                                <tr>
                                    <th><Text val="VOTING_LOG_WHEN" /></th>
                                    <th><Text val="ADMIN_VOTING_BOT_REDACTED" /></th>
                                    <th><Text val="VOTING_LOG_SITE" /></th>
                                    <th><Text val="VOTING_LOG_OUTCOME" /></th>
                                    <th><Text val="VOTING_LOG_DURATION" /></th>
                                    <th><Text val="ADMIN_VOTING_LOG_NOTES" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                <For each={logs}>{(l) => (
                                    <tr>
                                        <td>{formatDateTime(l.startedUtc)}</td>
                                        <td style={{ 'font-family': 'var(--font-family-mono)' }}>{l.minecraftUsernameRedacted}</td>
                                        <td>{l.siteKey}</td>
                                        <td>
                                            <span class={`${styles.outcomeBadge} ${isPositiveOutcome(l.outcome) ? styles.outcomeOk : (l.outcome === 5 ? '' : styles.outcomeBad)}`}>
                                                <Text val={voteOutcomeKey(l.outcome)} />
                                            </span>
                                        </td>
                                        <td>{l.durationMs}ms</td>
                                        <td style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'max-width': '40rem', 'overflow': 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' }} title={l.rawNotes ?? ''}>
                                            {l.rawNotes ?? '-'}
                                        </td>
                                    </tr>
                                )}</For>
                            </tbody>
                        </table>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
